<script setup lang="ts">
/** 1.d Minutes of AGM. 분기 토글(director_rotation/remuneration_mode/declare_dividend). 서명 = [[chairman_name]] 변수. */
import { computed } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import { useAgmFill } from '../../aura/agm/cdd/useCddFill';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
function val(k: string) { return String(fill.values[k] ?? '').trim(); }
function setSel(k: string, v: string) { fill.setValue(k, val(k) === v ? '' : v); }
function on(k: string) { const v = val(k).toLowerCase(); return v === 'yes' || v === 'true' || v === '1'; }
/** 감사인 분기 — 미지정이면 기존 동작(재선임)으로 본다. Word 템플릿도 같은 기본값. */
const auditorMode = computed(() => (val('auditor_mode') === 'none' ? 'none' : 'reappoint'));
function toggle(k: string) { fill.setValue(k, on(k) ? 'no' : 'yes'); }
</script>

<template>
  <div class="agm-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div>(Incorporated in the Republic of Singapore)</div>
      <div>Unique Entity Number: <CddVarField field-key="company_uen" label="UEN" /></div>
      <div>(“the Company”)</div>
    </div>

    <p class="doc-p" style="margin-top:14px">Minutes of the Annual General Meeting of the Company held on <CddVarField field-key="meeting_date" label="Meeting date" /> at <CddVarField field-key="meeting_time" label="Meeting time" /> at <CddVarField field-key="meeting_venue" label="Meeting venue" />.</p>

    <p class="sec-title">PRESENT</p>
    <p class="doc-p">As per Attendance Sheet attached.</p>

    <p class="sec-title">CHAIRMAN</p>
    <p class="doc-p"><CddVarField field-key="chairman_name" label="Chairman" /> was elected as Chairman of this meeting.</p>

    <p class="sec-title">NOTICE</p>
    <p class="doc-p">The notice convening the meeting was, with the consent of the members taken as read.</p>

    <p class="sec-title">DIRECTORATE</p>
    <div class="branch">
      <div class="branch-pick">
        <span class="doc-toggle" :class="{ off: val('director_rotation') !== 'none' }" @click="setSel('director_rotation','none')">No retirement</span>
        <span class="doc-toggle" :class="{ off: val('director_rotation') !== 'single' }" @click="setSel('director_rotation','single')">One retires</span>
        <span class="doc-toggle" :class="{ off: val('director_rotation') !== 'multiple' }" @click="setSel('director_rotation','multiple')">Multiple retire</span>
      </div>
      <p class="doc-p opt" v-if="val('director_rotation') === 'none'">IT WAS NOTED - That there will be no retirement of director by rotation as the company has only one director.</p>
      <p class="doc-p opt" v-else-if="val('director_rotation') === 'single'">RESOLVED – That <CddVarField field-key="retiring_director_name" label="Retiring director" /> who retires by rotation be and is hereby re-elected as Director of the Company.</p>
      <p class="doc-p opt" v-else-if="val('director_rotation') === 'multiple'">RESOLVED – That <CddVarField field-key="retiring_directors" label="Retiring directors (e.g. A and B)" /> who retire by rotation be and are hereby re-elected as Directors of the Company.</p>
    </div>

    <p class="sec-title">ACCOUNTS</p>
    <p class="doc-p">RESOLVED – That the Unaudited Financial Statements for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> together with the Directors’ Statement be and they are hereby approved and adopted.</p>

    <p class="sec-title">DIRECTORS’ REMUNERATION</p>
    <div class="branch">
      <div class="branch-pick">
        <span class="doc-toggle" :class="{ off: val('remuneration_mode') !== 'none' }" @click="setSel('remuneration_mode','none')">No remuneration</span>
        <span class="doc-toggle" :class="{ off: val('remuneration_mode') !== 'paid' }" @click="setSel('remuneration_mode','paid')">Approve payment</span>
      </div>
      <p class="doc-p opt" v-if="val('remuneration_mode') === 'none'">IT WAS NOTED - That there is no directors’ remuneration for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" />.</p>
      <p class="doc-p opt" v-else-if="val('remuneration_mode') === 'paid'">RESOLVED - That the Company do approve the payment of directors’ remuneration amounting to <CddVarField field-key="remuneration_amount" label="Remuneration amount" /> and/ or directors’ fees amounting to <CddVarField field-key="fees_amount" label="Fees amount" /> for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" />.</p>
    </div>

    <p class="sec-title">AUDITORS</p>
    <p class="doc-p">IT WAS NOTED – That the Company is a small company exempt from audit for its financial year ended <CddVarField field-key="financial_year_end" label="FYE" /> since it meets the requirements of not exceeding revenue/assets of $10 million and /or 50 employees for the period and in the manner stated in Section 205C of the Singapore Companies Act 1967.</p>
    <div class="branch">
      <div class="branch-pick">
        <span class="doc-toggle" :class="{ off: auditorMode !== 'reappoint' }" @click="fill.setValue('auditor_mode','reappoint')">Re-appoint auditors</span>
        <span class="doc-toggle" :class="{ off: auditorMode !== 'none' }" @click="fill.setValue('auditor_mode','none')">No auditors needed</span>
      </div>
      <p class="doc-p opt" v-if="auditorMode === 'reappoint'">RESOLVED - That <CddVarField field-key="auditor_name" label="Auditor firm" />, the retiring Auditors, be and they are hereby re-appointed as Auditors of the Company, at a remuneration to be determined by the Directors in the event that audit is required for the ensuing year.</p>
      <p class="doc-p opt" v-else>It was resolved that no auditors need to be appointed for the ensuing year.</p>
    </div>

    <div class="branch">
      <div class="branch-pick">
        <span class="doc-toggle" :class="{ off: !on('declare_dividend') }" @click="toggle('declare_dividend')">{{ on('declare_dividend') ? '☑' : '☐' }} Declare dividend</span>
      </div>
      <!-- 배당 미적용이면 'DIVIDEND' 제목까지 숨긴다(고객 요청). Word 템플릿도 제목부터 조건 블록. -->
      <p class="sec-title" v-if="on('declare_dividend')">DIVIDEND</p>
      <p class="doc-p opt" v-if="on('declare_dividend')">RESOLVED – That the Company do declare a one-tier tax exempt Final Dividend of <CddVarField field-key="dividend_per_share" label="Dividend per share" /> per ordinary share on the paid up of <CddVarField field-key="paid_up_shares" label="Paid-up shares" /> ordinary shares totaling <CddVarField field-key="dividend_total" label="Dividend total" /> in respect of the financial year ended <CddVarField field-key="financial_year_end" label="FYE" />.</p>
    </div>

    <p class="sec-title">AUTHORITY TO ALLOT AND ISSUE SHARES</p>
    <p class="doc-p">RESOLVED – That pursuant to Section 161 of the Singapore Companies Act 1967, the Directors be and are hereby authorised to allot and issue shares in the capital of the Company to such persons at any time and upon on such terms and conditions and with such rights or restrictions as they may think fit to impose and that such authority shall continue in force until the conclusion of the next Annual General Meeting or the expiration of the period within which the next Annual General Meeting of the Company is required by law to be held, whichever is earlier.</p>

    <p class="sec-title">TERMINATION OF MEETING</p>
    <p class="doc-p">There being no other business, the meeting terminated with a vote of thanks to the Chairman.</p>

    <p class="doc-p" style="margin-top:14px">APPROVED THIS <CddVarField field-key="minutes_approved_date" label="Approved date" /></p>

    <div class="sign-block">
      <div class="sign-nm"><CddVarField field-key="chairman_name" label="Chairman" /></div>
      <div class="sign-title">CHAIRMAN</div>
    </div>
  </div>
</template>

<style scoped>
.doc-company { font-weight: 800; font-size: 13.5px; }
.branch { margin: 4px 0; }
.branch-pick { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
.opt { background: #f7faff; border-left: 3px solid #cfe0ff; padding-left: 8px; }
.sign-block { margin-top: 24px; }
.sign-nm { font-weight: 700; }
.sign-title { font-size: 11px; color: #42506a; }
</style>
